// Access personas — named bundles of (role + module grants) so an admin
// reasons about PEOPLE, not 18 cryptic module checkboxes. A persona is a
// pure preset over the existing primitives: applying one just sets role +
// allowedModules via PATCH /api/users. "Custom" is always available for
// anything off-menu.
//
// The single source of truth for "who sees what" in the funnel:
//   - Sales rep / CS are VIEWERS → automatically scoped to their HubSpot
//     book (their owned orgs + teammates). That scoping is role-based
//     (viewer), so a rep MUST be a viewer to be limited to their accounts.
//   - Marketing is a viewer too, but with no 'orgs' grant — they live in
//     the aggregate channel/funnel dashboards, which are not per-account.
//   - Admin bypasses every module gate and sees all accounts.

export interface Persona {
  key: 'sales_rep' | 'sales_lead' | 'cs' | 'cs_lead' | 'marketing' | 'product' | 'bdr' | 'admin';
  label: string;
  blurb: string;
  role: 'admin' | 'viewer';
  /** Module grants; ignored when role === 'admin' (admins bypass). */
  modules: string[];
  /**
   * Pure-presentation hints for the role-based rail shell (redesign): the
   * GTM-spine section keys this role's Home opens to on first paint. These do
   * NOT affect access (that's `modules`) — only which accordion groups are
   * expanded by default. Section keys mirror lib/rail-nav RAIL_SECTIONS.
   */
  openJobs?: string[];
  /**
   * When true, selecting this persona in Invite/Edit should default
   * "Owned accounts only" OFF — lead personas rely on HubSpot manager
   * downline (or "Supervises teams") for team-wide reach. Rep personas leave
   * the checkbox alone so an admin can still pin shared-team peers.
   */
  preferTeamScope?: boolean;
}

export const PERSONAS: Persona[] = [
  {
    key: 'sales_rep',
    label: 'Sales rep',
    blurb: 'Pulse + assigned leads and campaigns + their own accounts (and each account’s usage) + quarterly Forecast + the Pipeline deal board (own book). No marketing, paid ads, or insights.',
    role: 'viewer',
    modules: ['orgs', 'pipeline', 'forecast', 'sales'],
    openJobs: ['sell'],
  },
  {
    key: 'sales_lead',
    label: 'Sales lead',
    // NB: both sales personas now grant the Pipeline board. The remaining
    // difference is TEAM BOOK, not pages — and the persona still cannot
    // deliver that by itself. Which accounts a lead sees follows HubSpot
    // manager downline (or an admin sets "Supervises teams" in Edit user).
    //
    // `team` is a persona-identity grant (not a page). matchPersona is an
    // exact module-set match and sales_rep is first in PERSONAS; without a
    // distinct set, leads would be mislabeled as reps. `team` does not
    // widen account scope.
    blurb: 'The sales-rep view plus team-book reach. To include their reps’ accounts, set “Supervises teams” in Edit user (or rely on HubSpot manager downline).',
    role: 'viewer',
    modules: ['orgs', 'pipeline', 'forecast', 'sales', 'team'],
    openJobs: ['sell', 'expand'],
    preferTeamScope: true,
  },
  {
    key: 'cs',
    label: 'Customer Success',
    blurb: 'The sales-rep view plus Milestones, Retention, Cohorts, and in-product Usage/adoption.',
    role: 'viewer',
    // `overview` gates the Usage page — the CS hub surfaces adoption as the
    // leading churn signal, so CS carries it. `milestones` gates the north-star
    // Milestones report — CS steers retention against those company milestones,
    // so CS carries it too (admin-only-by-default module, explicitly granted here).
    modules: ['orgs', 'cohorts', 'retention', 'overview', 'milestones'],
    openJobs: ['retain', 'engage'],
  },
  {
    key: 'cs_lead',
    label: 'CS lead',
    // Same page grant as CS + Pipeline (team roll-up / CS Managed+Scaled deals).
    // Account reach still follows HubSpot: secondary/supervised team membership
    // widens the book; do NOT pin "Owned accounts only" on leads.
    blurb: 'CS view plus the Pipeline deal board across the team’s book. Leave “Owned accounts only” off; use “Supervises teams” only if HubSpot doesn’t already mark them as a manager.',
    role: 'viewer',
    modules: ['orgs', 'cohorts', 'retention', 'overview', 'milestones', 'pipeline'],
    openJobs: ['retain', 'engage'],
    preferTeamScope: true,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    blurb: 'Pulse + Paid Ads, Activation Funnel, Spend Coverage, Insights and Cohorts. No customer accounts.',
    role: 'viewer',
    // `milestones` gates the Spend Coverage payback report — the Marketing
    // hub's headline metric, so Marketing carries it.
    modules: ['acquisition', 'paid_ads', 'activation', 'insights', 'cohorts', 'milestones'],
    openJobs: ['acquire', 'activate'],
  },
  {
    key: 'product',
    label: 'Product',
    blurb: 'The company-wide self-serve analyst: the activation funnel, FTUE replay insights, engagement, feature adoption and stickiness. No customer accounts, no spend.',
    role: 'viewer',
    modules: ['activation', 'ftue', 'engagement', 'overview', 'cohorts', 'insights'],
    openJobs: ['activate', 'engage'],
  },
  {
    key: 'bdr',
    label: 'BDR / Prospecting',
    blurb: 'The unowned-signal worklist (Prospect queue) — accounts showing buying intent, ranked — plus the account list.',
    role: 'viewer',
    modules: ['prospect', 'orgs'],
    openJobs: ['prospect'],
  },
  {
    key: 'admin',
    label: 'Admin',
    blurb: 'Everything, across all accounts.',
    role: 'admin',
    modules: [],
  },
];

function sameSet(a: string[], b: string[]): boolean {
  // Dedupe both sides so a malformed/legacy module list with duplicates
  // (only reachable via a hand-crafted PATCH — the UI builds from a Set)
  // can't false-match a persona and mislabel the user in Settings.
  const sa = new Set(a);
  const sb = new Set(b);
  return sa.size === sb.size && [...sa].every((x) => sb.has(x));
}

/** Which persona a user's (role, modules) exactly matches — else 'custom'. */
export function matchPersona(role: 'admin' | 'viewer', modules: string[]): Persona['key'] | 'custom' {
  if (role === 'admin') return 'admin';
  const found = PERSONAS.find((p) => p.role === role && sameSet(p.modules, modules));
  return found ? found.key : 'custom';
}

/**
 * The GTM-spine sections a user's persona opens to by default in the rail
 * shell (pure presentation — see Persona.openJobs). Admin and custom users get
 * none, so the admin rail stays collapsed (the user's call). Accepts the app's
 * full role enum; only admin-vs-viewer is meaningful here.
 */
export function personaOpenJobs(role: 'admin' | 'editor' | 'viewer' | null, modules: string[]): string[] {
  if (role !== 'admin' && role !== 'viewer') return [];
  const key = matchPersona(role, modules);
  if (key === 'custom') return [];
  return PERSONAS.find((p) => p.key === key)?.openJobs ?? [];
}

export function personaLabel(key: Persona['key'] | 'custom'): string {
  if (key === 'custom') return 'Custom';
  return PERSONAS.find((p) => p.key === key)?.label ?? 'Custom';
}

// Module → plain-language capability. 'overview' gates the Usage tab too
// (a known coupling), so it reads as Overview + Usage. Pulse is ungated —
// everyone gets it — so it's always listed first.
const MODULE_CAPABILITY: Record<string, string> = {
  overview: 'Overview + Usage',
  acquisition: 'Acquisition',
  paid_ads: 'Paid Ads',
  activation: 'Activation Funnel',
  ftue: 'FTUE Replays',
  insights: 'Insights',
  cohorts: 'Cohorts',
  retention: 'Retention',
  milestones: 'Milestones',
  pipeline: 'Pipeline',
  forecast: 'Forecast',
  prospect: 'Prospect queue',
  sales: 'Sales',
  search: 'Search',
  scoring: 'Scoring',
  tools: 'Tools',
  engagement: 'Engagement',
  expansion: 'Expansion',
  churn: 'Churn',
  winback: 'Win Back',
  risk: 'Risk',
  live: 'Live',
};

/**
 * Plain-language list of what a user can actually see — the answer to
 * "understand what they view". `accountScoped` (true for viewers) turns the
 * Orgs grant into "their accounts" rather than "all accounts".
 */
export function describeAccess(
  role: 'admin' | 'viewer',
  modules: string[],
  accountScoped: boolean,
): string[] {
  if (role === 'admin') return ['Everything (admin) · all accounts'];
  const caps = ['Pulse'];
  const mset = new Set(modules);
  if (mset.has('orgs')) {
    caps.push(accountScoped ? 'Their accounts + usage' : 'All accounts + usage');
  }
  for (const key of Object.keys(MODULE_CAPABILITY)) {
    if (key === 'overview' && !mset.has('overview')) continue;
    if (mset.has(key)) caps.push(MODULE_CAPABILITY[key]);
  }
  return caps;
}
